<template>
  <div class="vpp-page py-3">
    <header class="vpp-toolbar">
      <div class="vpp-toolbar__title">
        <span class="vpp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>COD Payments</h1>
          <p>Search, mark, and report COD payment activity</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="vpp-alert" @click.native="errorMessage = ''">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="vpp-alert" @click.native="successMessage = ''">{{ successMessage }}</CAlert>

    <div class="vpp-tab-bar" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        :class="['vpp-tab', { 'is-active': activeTab === tab.key }]"
        :aria-selected="activeTab === tab.key"
        @click="setActiveTab(tab.key)"
      >
        <span class="vpp-tab__label">{{ tab.label }}</span>
      </button>
    </div>

    <!-- ════════════════════ SEARCH PAYMENTS ════════════════════ -->
    <section v-if="activeTab === 'search'" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Search Payments</h2>
      </header>
      <div class="vpp-section__body">
        <div class="vpp-filters">
          <div class="vpp-filter">
            <label class="vpp-label">Select Search Method</label>
            <AppDropdown
              :value="search.method"
              :options="searchMethodOptions"
              @input="search.method = $event"
            />
          </div>
          <div class="vpp-filter">
            <label class="vpp-label">{{ searchInputLabel }}</label>
            <AppInput
              no-margin
              :value="search.value"
              :placeholder="searchInputLabel"
              @update:value="search.value = $event"
              @keyup.enter.native="runSearch"
            />
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--primary" :disabled="search.loading" @click="runSearch">
              {{ search.loading ? 'Searching…' : 'Search' }}
            </CButton>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      v-if="activeTab === 'search'"
      title="Search Results"
      :items="search.results"
      :columns="searchColumns"
      :loading="search.loading"
      :sortable="false"
      :show-count="true"
      empty-text="No matching orders found. Try a different search."
    >
      <template #cell-grand_total="{ value }">
        {{ formatINR(value) }}
      </template>
      <template #cell-payment_status="{ value }">
        <span :class="['status-pill', paymentStatusClass(value)]">{{ formatLabel(value) }}</span>
      </template>
      <template #cell-action="{ row }">
        <CButton size="sm" class="vpp-row-btn" @click="openOrderDetails(row)">
          View
        </CButton>
      </template>
    </BaseTable>

    <!-- ════════════════════ BULK ════════════════════ -->
    <section v-if="activeTab === 'bulk'" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Bulk Mark As Paid</h2>
      </header>
      <div class="vpp-section__body">
        <p class="vpp-helper">Upload a post-office COD Commission Report PDF or a COD CSV to mark multiple orders as paid in one go.</p>
        <div class="vpp-filters">
          <div class="vpp-filter">
            <label class="vpp-label">Upload Type</label>
            <AppDropdown
              :value="bulk.kind"
              :options="bulkKindOptions"
              @input="onBulkKindChange"
            />
          </div>
          <div class="vpp-filter">
            <label class="vpp-label">
              Payment Date
              <span v-if="bulk.kind === 'cod_pdf'" class="vpp-label__hint">(overrides delivered date)</span>
            </label>
            <SingleDatePicker :value="bulk.paymentDate" @input="bulk.paymentDate = $event" />
          </div>
          <div class="vpp-filter vpp-filter--file">
            <label class="vpp-label">{{ bulkFileLabel }}</label>
            <label class="vpp-file" :class="{ 'is-empty': !bulk.file }">
              <input
                ref="bulkFileInput"
                type="file"
                class="vpp-file__native"
                :accept="bulkFileAccept"
                @change="onBulkFileSelect"
              />
              <span class="vpp-file__btn">Choose File</span>
              <span class="vpp-file__name">{{ bulk.file ? bulk.file.name : 'No file chosen' }}</span>
            </label>
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--primary" :disabled="!bulk.canParse || bulk.parsing" @click="parseBulkFile">
              {{ bulk.parsing ? 'Parsing…' : 'Parse File' }}
            </CButton>
          </div>
        </div>
      </div>
    </section>

    <!-- COD PDF report meta strip -->
    <section v-if="activeTab === 'bulk' && bulk.kind === 'cod_pdf' && bulk.report.loaded" class="vpp-kpi-row vpp-kpi-row--four">
      <div class="vpp-kpi vpp-kpi--indigo">
        <div class="vpp-kpi__label">Total Articles</div>
        <div class="vpp-kpi__value">{{ bulk.matched.length + bulk.unmatched.length }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--emerald">
        <div class="vpp-kpi__label">Matched</div>
        <div class="vpp-kpi__value">{{ bulk.matched.length }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--violet">
        <div class="vpp-kpi__label">Report Total</div>
        <div class="vpp-kpi__value">{{ bulk.report.totalAmount != null ? formatINR(bulk.report.totalAmount) : '—' }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--cyan">
        <div class="vpp-kpi__label">Report Date</div>
        <div class="vpp-kpi__value vpp-kpi__value--sm">{{ bulk.report.reportDate || '—' }}</div>
      </div>
    </section>

    <!-- Matched orders — COD PDF flavour -->
    <section v-if="activeTab === 'bulk' && bulk.kind === 'cod_pdf' && bulk.matched.length" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Matched Orders ({{ bulk.matched.length }})</h2>
        <div class="vpp-section__actions">
          <span v-if="codSelectedCount" class="cod-selection">{{ codSelectedCount }} selected</span>
          <CButton
            class="vpp-btn vpp-btn--primary"
            :disabled="bulk.submitting || !codSelectedCount"
            @click="submitCodBulk('selected')"
          >
            {{ bulk.submitting ? 'Marking…' : `Approve Selected${codSelectedCount ? ' (' + codSelectedCount + ')' : ''}` }}
          </CButton>
          <CButton
            class="vpp-btn vpp-btn--ghost"
            :disabled="bulk.submitting || !codPendingCount"
            @click="submitCodBulk('all_pending')"
          >
            Approve All Pending ({{ codPendingCount }})
          </CButton>
        </div>
      </header>
      <div class="vpp-section__body">
        <BaseTable
          :items="bulk.matched"
          :columns="codBulkColumns"
          :sortable="false"
          :show-count="false"
          :row-class-fn="codRowClass"
          empty-text="No matched orders."
        >
          <template #header-select>
            <input
              type="checkbox"
              class="cod-check"
              :checked="codAllPendingSelected"
              :indeterminate.prop="codSomeSelected"
              :disabled="!codPendingCount"
              @change="toggleSelectAllCod($event.target.checked)"
            />
          </template>
          <template #cell-select="{ row }">
            <input
              type="checkbox"
              class="cod-check"
              :checked="!!bulk.selected[row.tracking_id]"
              :disabled="row.already_paid"
              @change="toggleCodRow(row, $event.target.checked)"
            />
          </template>
          <template #cell-tracking_id="{ value }">
            <span class="cod-tracking">{{ value }}</span>
          </template>
          <template #cell-delivered_date_display="{ value }">{{ value || '—' }}</template>
          <template #cell-cod_amount="{ value, row }">
            <span :class="['cod-amt', { 'cod-amt--mismatch': row.amount_mismatch }]">{{ formatINR(value) }}</span>
            <span v-if="row.amount_mismatch" class="cod-mismatch-note" :title="`Order amount: ${formatINR(row.order_amount)}`">
              ≠ {{ formatINR(row.order_amount) }}
            </span>
          </template>
          <template #cell-order_number="{ value }">{{ value || '—' }}</template>
          <template #cell-contact_name="{ value }">{{ value || '—' }}</template>
          <template #cell-payment_status="{ value, row }">
            <span :class="['status-pill', paymentStatusClass(value)]">
              {{ formatLabel(value) }}
            </span>
            <div v-if="row.already_paid && row.payment_date" class="cod-paid-on">
              on {{ formatModalDate(row.payment_date) }}
            </div>
          </template>
          <template #cell-action="{ row }">
            <button
              v-if="row.already_paid"
              type="button"
              class="cod-action-btn cod-action-btn--disabled"
              disabled
            >
              Paid
            </button>
            <button
              v-else
              type="button"
              class="cod-action-btn"
              :disabled="bulk.rowSubmitting[row.tracking_id]"
              @click="recordCodPayment(row)"
            >
              {{ bulk.rowSubmitting[row.tracking_id] ? 'Saving…' : 'Record Payment' }}
            </button>
          </template>
        </BaseTable>
      </div>
    </section>

    <!-- Unmatched articles — COD PDF flavour -->
    <section v-if="activeTab === 'bulk' && bulk.kind === 'cod_pdf' && bulk.unmatched.length" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Unmatched Articles ({{ bulk.unmatched.length }})</h2>
      </header>
      <div class="vpp-section__body">
        <p class="vpp-helper">These article numbers were in the PDF but have no matching order (by tracking ID) in the system.</p>
        <BaseTable
          :items="bulk.unmatched"
          :columns="codUnmatchedColumns"
          :sortable="false"
          :show-count="false"
          empty-text="No unmatched articles."
        >
          <template #cell-tracking_id="{ value }"><span class="cod-tracking">{{ value }}</span></template>
          <template #cell-delivered_date_display="{ value }">{{ value || '—' }}</template>
          <template #cell-cod_amount="{ value }">{{ formatINR(value) }}</template>
        </BaseTable>
      </div>
    </section>

    <!-- Legacy CSV/EMO flow -->
    <section v-if="activeTab === 'bulk' && bulk.kind !== 'cod_pdf' && bulk.matched.length" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Matched Orders ({{ bulk.matched.length }})</h2>
        <div class="vpp-section__actions">
          <CButton class="vpp-btn vpp-btn--primary" :disabled="bulk.submitting || !bulk.paymentDate" @click="submitBulk">
            {{ bulk.submitting ? 'Marking…' : 'Mark All As Paid' }}
          </CButton>
        </div>
      </header>
      <div class="vpp-section__body">
        <BaseTable
          :items="bulk.matched"
          :columns="bulkColumns"
          :sortable="false"
          :show-count="false"
          empty-text="No matched orders."
        >
          <template #cell-amount="{ value }">{{ formatINR(value) }}</template>
        </BaseTable>
      </div>
    </section>

    <!-- ════════════════════ PENDING PAYMENTS ════════════════════ -->
    <section v-if="activeTab === 'pending'" class="vpp-section">
      <header class="vpp-section__head">
        <h2>Filters</h2>
      </header>
      <div class="vpp-section__body">
        <div class="vpp-filters">
          <div class="vpp-filter">
            <label class="vpp-label">From Date</label>
            <SingleDatePicker :value="pending.fromDate" @input="pending.fromDate = $event" />
          </div>
          <div class="vpp-filter">
            <label class="vpp-label">To Date</label>
            <SingleDatePicker :value="pending.toDate" @input="pending.toDate = $event" />
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--primary" :disabled="pending.loading" @click="loadPending('view')">
              {{ pending.loading ? 'Loading…' : 'Apply' }}
            </CButton>
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--ghost" :disabled="pending.downloading" @click="loadPending('download')">
              {{ pending.downloading ? 'Preparing…' : downloadCsvLabel }}
            </CButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="activeTab === 'pending'" class="vpp-aging-row">
      <button
        v-for="card in pendingCards"
        :key="card.key"
        type="button"
        :class="['vpp-aging-card', `vpp-aging-card--${card.key}`, { 'is-active': pending.bucket === card.key }]"
        @click="setPendingBucket(card.key)"
      >
        <span class="vpp-aging-card__top">
          <span class="vpp-aging-card__label">{{ card.label }}</span>
          <span class="vpp-aging-card__range">{{ card.range }}</span>
        </span>
        <span class="vpp-aging-card__count">{{ pendingSummary[card.key].count }}</span>
        <span class="vpp-aging-card__amount">{{ formatINR(pendingSummary[card.key].sum) }}</span>
      </button>
    </section>

    <BaseTable
      v-if="activeTab === 'pending'"
      title="Pending Payments"
      :items="visiblePendingRows"
      :columns="pendingColumns"
      :loading="pending.loading"
      :row-class-fn="pendingRowClass"
      :default-sort="{ column: 'age', direction: 'desc' }"
      :show-count="true"
      class="pending-table"
      empty-text="No pending payments for selected range."
      :paginated="true"
      :page-size="25"
    >
      <template #toolbar>
        <div class="cmp-filter" role="group" aria-label="Complaint filter">
          <button
            type="button"
            :class="['cmp-filter__chip', { 'is-active': pending.complaintFilter === 'filed' }]"
            @click="setComplaintFilter('filed')"
          >
            <span class="cmp-filter__dot cmp-filter__dot--filed" aria-hidden="true"></span>
            Filed
            <span class="cmp-filter__num">{{ complaintSummary.filed.count }}</span>
          </button>
          <button
            type="button"
            :class="['cmp-filter__chip', { 'is-active': pending.complaintFilter === 'awaiting' }]"
            @click="setComplaintFilter('awaiting')"
          >
            <span class="cmp-filter__dot cmp-filter__dot--awaiting" aria-hidden="true"></span>
            Awaiting
            <span class="cmp-filter__num">{{ complaintSummary.awaiting.count }}</span>
          </button>
        </div>
      </template>
      <template #cell-age="{ row }">
        <span :class="['age-pill', `age-pill--${row.bucket}`]">{{ pendingAgeLabel(row.age) }}</span>
      </template>
      <template #cell-grand_total="{ value }">{{ formatINR(value) }}</template>
      <template #cell-placed_by="{ value }">
        <span class="placed-by">{{ value || '—' }}</span>
      </template>
      <template #cell-complaint="{ row }">
        <button
          v-if="row.cod_complaint_id"
          type="button"
          class="cmp-pill cmp-pill--filed"
          :title="`Filed ${formatModalDate(row.cod_complaint_date)}${row.cod_complaint_by ? ' by ' + row.cod_complaint_by : ''}`"
          @click="openComplaintModal(row)"
        >
          ✓ {{ row.cod_complaint_id }}
        </button>
        <button
          v-else-if="row.bucket === 'overdue'"
          type="button"
          class="cmp-pill cmp-pill--file"
          @click="openComplaintModal(row)"
        >
          File complaint
        </button>
        <span v-else class="cmp-pill cmp-pill--none">—</span>
      </template>
      <template #cell-action="{ row }">
        <CButton size="sm" class="vpp-row-btn" @click="openOrderDetails(row)">
          View
        </CButton>
      </template>
    </BaseTable>

    <!-- ════════════════════ VPP PAYMENT REPORT ════════════════════ -->
    <section v-if="activeTab === 'paymentReport'" class="vpp-section">
      <header class="vpp-section__head">
        <h2>COD Payment Report</h2>
      </header>
      <div class="vpp-section__body">
        <div class="vpp-filters">
          <div class="vpp-filter">
            <label class="vpp-label">Payment Date</label>
            <SingleDatePicker :value="paymentReport.date" @input="paymentReport.date = $event" />
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--primary" :disabled="paymentReport.loading" @click="loadPaymentReport">
              {{ paymentReport.loading ? 'Loading…' : 'Apply' }}
            </CButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="activeTab === 'paymentReport' && paymentReport.loaded" class="vpp-kpi-row vpp-kpi-row--four">
      <div class="vpp-kpi vpp-kpi--indigo">
        <div class="vpp-kpi__label">MAP Orders</div>
        <div class="vpp-kpi__value">{{ paymentReport.mapCount }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--cyan">
        <div class="vpp-kpi__label">MAP Total</div>
        <div class="vpp-kpi__value">{{ formatINR(paymentReport.mapSum) }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--violet">
        <div class="vpp-kpi__label">MR Orders</div>
        <div class="vpp-kpi__value">{{ paymentReport.mrCount }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--emerald">
        <div class="vpp-kpi__label">MR Total</div>
        <div class="vpp-kpi__value">{{ formatINR(paymentReport.mrSum) }}</div>
      </div>
    </section>

    <section v-if="activeTab === 'paymentReport' && paymentReport.loaded" class="vpp-section">
      <div class="vpp-section__body">
        <div class="vpp-filters">
          <div class="vpp-filter vpp-filter--full">
            <label class="vpp-label">Search</label>
            <AppInput
              no-margin
              :value="paymentReport.query"
              placeholder="Search by tracking ID, order number, customer, phone, method or amount…"
              @update:value="paymentReport.query = $event"
            />
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      v-if="activeTab === 'paymentReport' && paymentReport.loaded"
      :title="`MAP Orders (${filteredMapOrders.length})`"
      :items="filteredMapOrders"
      :columns="paymentReportColumns"
      :sortable="false"
      :show-count="false"
      :empty-text="paymentReport.query ? 'No MAP orders match your search.' : 'No MAP orders for this date.'"
    >
      <template #cell-tracking_id="{ value }">{{ value || '—' }}</template>
      <template #cell-grand_total="{ value }">{{ formatINR(value) }}</template>
    </BaseTable>

    <BaseTable
      v-if="activeTab === 'paymentReport' && paymentReport.loaded"
      :title="`MR Orders (${filteredMrOrders.length})`"
      :items="filteredMrOrders"
      :columns="paymentReportColumns"
      :sortable="false"
      :show-count="false"
      :empty-text="paymentReport.query ? 'No MR orders match your search.' : 'No MR orders for this date.'"
    >
      <template #cell-tracking_id="{ value }">{{ value || '—' }}</template>
      <template #cell-grand_total="{ value }">{{ formatINR(value) }}</template>
    </BaseTable>

    <!-- ════════════════════ VPP SALES REPORT ════════════════════ -->
    <section v-if="activeTab === 'salesReport'" class="vpp-section">
      <header class="vpp-section__head">
        <h2>VPP Sales Report</h2>
      </header>
      <div class="vpp-section__body">
        <div class="vpp-filters">
          <div class="vpp-filter">
            <label class="vpp-label">From Date</label>
            <SingleDatePicker :value="salesReport.fromDate" @input="salesReport.fromDate = $event" />
          </div>
          <div class="vpp-filter">
            <label class="vpp-label">To Date</label>
            <SingleDatePicker :value="salesReport.toDate" @input="salesReport.toDate = $event" />
          </div>
          <div class="vpp-filter vpp-filter--action">
            <label class="vpp-label" aria-hidden="true">&nbsp;</label>
            <CButton class="vpp-btn vpp-btn--primary" :disabled="salesReport.loading" @click="loadSalesReport">
              {{ salesReport.loading ? 'Loading…' : 'Apply' }}
            </CButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="activeTab === 'salesReport' && salesReport.loaded" class="vpp-kpi-row">
      <div class="vpp-kpi vpp-kpi--indigo">
        <div class="vpp-kpi__label">Total Sales Orders</div>
        <div class="vpp-kpi__value">{{ salesReport.count }}</div>
      </div>
      <div class="vpp-kpi vpp-kpi--emerald">
        <div class="vpp-kpi__label">Total Sales Amount</div>
        <div class="vpp-kpi__value">{{ formatINR(salesReport.amount) }}</div>
      </div>
    </section>

    <!-- ════════════════════ ORDER DETAILS MODAL ════════════════════ -->
    <CModal
      :show.sync="detailsModal.show"
      size="lg"
      :centered="true"
      title="Order Details"
      class="view-order-modal"
    >
      <div v-if="detailsModal.loading" class="vo-state">Loading order details…</div>
      <div v-else-if="detailsModal.error" class="vo-state vo-state--error">{{ detailsModal.error }}</div>
      <div v-else-if="detailsModal.order" class="vo-body">
        <div class="vo-section">
          <div class="vo-row"><span class="vo-key">Order ID</span><span class="vo-val">{{ detailsModal.order.order_number || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Name</span><span class="vo-val">{{ detailsModal.order.contact_name || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Address</span><span class="vo-val">{{ detailsModal.order.contact_address || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Post Office</span><span class="vo-val">{{ detailsModal.order.contact_postofficename || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Pincode</span><span class="vo-val">{{ detailsModal.order.contact_pincode || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Phone</span><span class="vo-val">{{ detailsModal.order.contact_phone || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Tracking ID</span><span class="vo-val">{{ detailsModal.order.tracking_id || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Amount</span><span class="vo-val">{{ formatINR(detailsModal.order.grand_total) }}</span></div>
        </div>

        <div class="vo-items-wrap">
          <table class="vo-items">
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!detailsModal.order.order_items || !detailsModal.order.order_items.length">
                <td colspan="4" class="vo-items__empty">No items</td>
              </tr>
              <tr v-for="(item, idx) in (detailsModal.order.order_items || [])" :key="`vo-item-${idx}`">
                <td>{{ item.name || item.item_name || '—' }}</td>
                <td>{{ item.price != null ? item.price : '—' }}</td>
                <td>{{ item.quantity != null ? item.quantity : '—' }}</td>
                <td>{{ orderItemTotal(item) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="vo-grid">
          <div class="vo-row"><span class="vo-key">Order Type</span><span class="vo-val">{{ formatLabel(detailsModal.order.order_type) }}</span></div>
          <div class="vo-row"><span class="vo-key">Total Bottles</span><span class="vo-val">{{ totalBottles(detailsModal.order) }}</span></div>
          <div class="vo-row"><span class="vo-key">Total Amount</span><span class="vo-val">{{ detailsModal.order.total != null ? detailsModal.order.total : '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping Charge</span><span class="vo-val">{{ detailsModal.order.shipping_charge != null ? detailsModal.order.shipping_charge : '0' }}</span></div>
          <div class="vo-row"><span class="vo-key">Discount</span><span class="vo-val">{{ detailsModal.order.discount != null ? detailsModal.order.discount : '0' }}</span></div>
          <div class="vo-row"><span class="vo-key">Grand Total</span><span class="vo-val">{{ detailsModal.order.grand_total != null ? detailsModal.order.grand_total : '—' }}</span></div>
          <div class="vo-row">
            <span class="vo-key">Shipping Method</span>
            <span class="vo-val">
              <span v-if="detailsModal.order.shipping_method" class="vo-pill vo-pill--shipping">{{ formatLabel(detailsModal.order.shipping_method) }}</span>
              <span v-else>—</span>
            </span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Payment Method</span>
            <span class="vo-val">
              <span v-if="detailsModal.order.payment_method" class="vo-pill vo-pill--payment">{{ formatLabel(detailsModal.order.payment_method) }}</span>
              <span v-else>—</span>
            </span>
          </div>
          <div class="vo-row"><span class="vo-key">Order Date</span><span class="vo-val">{{ formatModalDate(detailsModal.order.order_date || detailsModal.order.created_at) }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping Date</span><span class="vo-val">{{ formatModalDate(detailsModal.order.shipping_date) }}</span></div>
          <div class="vo-row">
            <span class="vo-key">Payment Status</span>
            <span class="vo-val">
              <span :class="['vo-pill', paymentBadgeClass(detailsModal.order.payment_status)]">{{ formatLabel(detailsModal.order.payment_status) }}</span>
            </span>
          </div>
          <div class="vo-row"><span class="vo-key">Payment Date</span><span class="vo-val">{{ formatModalDate(detailsModal.order.payment_date) }}</span></div>
          <div class="vo-row"><span class="vo-key">Placed By</span><span class="vo-val">{{ (detailsModal.order.user && detailsModal.order.user.name) || '—' }}</span></div>
        </div>

        <div class="vo-section vo-update">
          <div class="vo-row vo-row--update">
            <span class="vo-key">Update Payment Date</span>
            <span class="vo-val">
              <SingleDatePicker :value="detailsModal.updatePaymentDate" @input="detailsModal.updatePaymentDate = $event" />
            </span>
          </div>
        </div>
      </div>

      <template #footer>
        <CButton
          v-if="isCodComplaintRelevant(detailsModal.order)"
          color="info"
          variant="outline"
          class="vpp-modal-complaint-btn"
          :disabled="!detailsModal.order"
          @click="openComplaintModal(detailsModal.order)"
        >
          PO Complaint
        </CButton>
        <CButton
          color="danger"
          :disabled="!detailsModal.order || detailsModal.removing || detailsModal.updating"
          @click="removePaymentFromModal"
        >
          {{ detailsModal.removing ? 'Removing…' : 'Remove Payment' }}
        </CButton>
        <CButton
          color="success"
          :disabled="!detailsModal.order || !detailsModal.updatePaymentDate || detailsModal.removing || detailsModal.updating"
          @click="updatePaymentDateFromModal"
        >
          {{ detailsModal.updating ? 'Saving…' : 'Update Payment Date' }}
        </CButton>
        <CButton color="secondary" variant="outline" @click="closeOrderDetails">Close</CButton>
      </template>
    </CModal>

    <!-- ════════════════════ POST OFFICE COMPLAINT MODAL ════════════════════ -->
    <CModal
      :show.sync="complaintModal.show"
      size="lg"
      :centered="true"
      title="Post Office Complaint"
      class="cmp-modal"
    >
      <div class="cmp-body">
        <div class="cmp-summary">
          <div class="cmp-summary__main">
            <span class="cmp-summary__order">{{ complaintModal.header.orderNumber }}</span>
            <span v-if="complaintModal.header.age != null" :class="['age-pill', `age-pill--${complaintModal.header.age >= 30 ? 'overdue' : (complaintModal.header.age >= 15 ? 'aging' : 'fresh')}`]">
              {{ pendingAgeLabel(complaintModal.header.age) }}
            </span>
            <span :class="['vo-pill', complaintModal.filed ? 'vo-pill--green' : 'vo-pill--yellow']">
              {{ complaintModal.filed ? 'Filed' : 'Not filed' }}
            </span>
          </div>
          <div class="cmp-summary__meta">
            <span>{{ complaintModal.header.customer }}</span>
            <span class="cmp-summary__dot">·</span>
            <span>{{ complaintModal.header.postOffice }}</span>
            <span class="cmp-summary__dot">·</span>
            <span class="cmp-summary__amt">{{ formatINR(complaintModal.header.amount) }}</span>
          </div>
        </div>

        <CAlert v-if="complaintModal.error" color="danger" show class="cmp-alert">{{ complaintModal.error }}</CAlert>

        <div class="cmp-field">
          <div class="cmp-field__label-row">
            <label class="cmp-label">Draft message — copy into the post office complaint portal</label>
            <button type="button" class="cmp-copy-btn cmp-copy-btn--lg" @click="copyComplaintDraft">
              {{ complaintModal.copied ? 'Copied ✓' : 'Copy message' }}
            </button>
          </div>
          <AppInput
            no-margin
            multiline
            :rows="6"
            :value="complaintModal.draft"
            @update:value="complaintModal.draft = $event"
          />
        </div>

        <div class="cmp-grid">
          <div class="cmp-field">
            <label class="cmp-label">Complaint ID <span class="cmp-req">*</span></label>
            <AppInput
              no-margin
              :value="complaintModal.id"
              placeholder="e.g. PO/COD/2026/0456"
              @update:value="complaintModal.id = $event"
            />
          </div>
          <div class="cmp-field">
            <label class="cmp-label">Date Filed</label>
            <SingleDatePicker :value="complaintModal.date" @input="complaintModal.date = $event" />
          </div>
        </div>

        <div class="cmp-field">
          <label class="cmp-label">Notes (optional)</label>
          <AppInput
            no-margin
            multiline
            :rows="2"
            :value="complaintModal.notes"
            placeholder="Any extra context for this complaint…"
            @update:value="complaintModal.notes = $event"
          />
        </div>

        <div v-if="complaintModal.filed && complaintModal.by" class="cmp-meta">
          Filed by <strong>{{ complaintModal.by }}</strong> on {{ formatModalDate(complaintModal.date) }}
        </div>
      </div>

      <template #footer>
        <CButton
          v-if="complaintModal.filed"
          color="danger"
          variant="outline"
          :disabled="complaintModal.clearing || complaintModal.saving"
          @click="clearComplaint"
        >
          {{ complaintModal.clearing ? 'Clearing…' : 'Clear Complaint' }}
        </CButton>
        <CButton color="secondary" variant="outline" @click="closeComplaintModal">Close</CButton>
        <CButton
          color="success"
          :disabled="complaintModal.saving || !complaintModal.id"
          @click="saveComplaint"
        >
          {{ complaintModal.saving ? 'Saving…' : (complaintModal.filed ? 'Update Complaint' : 'Save Complaint') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import { buildComplaintDraft, copyToClipboard } from '@/utils/codComplaint'

function isoToday () {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function daysAgo (n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

// Aging buckets for pending COD payments, keyed off the order date.
// fresh: 0–14 days · aging: 15–29 days · overdue: 30+ days.
const PENDING_BUCKETS = {
  fresh:   { key: 'fresh',   label: 'Fresh',   range: '1–15 days' },
  aging:   { key: 'aging',   label: 'Aging',   range: '15–30 days' },
  overdue: { key: 'overdue', label: 'Overdue', range: '30+ days' }
}

function pendingBucketFor (age) {
  if (age == null) return 'fresh'
  if (age >= 30) return 'overdue'
  if (age >= 15) return 'aging'
  return 'fresh'
}

export default {
  name: 'VppPayments',
  components: { AppDropdown, AppInput, SingleDatePicker, BaseTable },
  data () {
    const today = isoToday()
    return {
      activeTab: 'pending',
      errorMessage: '',
      successMessage: '',
      tabs: [
        { key: 'pending', label: 'Pending Payments' },
        { key: 'search', label: 'Search Payments' },
        { key: 'bulk', label: 'Bulk Mark As Paid' },
        { key: 'paymentReport', label: 'COD Payment Report' },
        { key: 'salesReport', label: 'COD Sales Report' }
      ],

      search: {
        method: 'order_number',
        value: '',
        loading: false,
        results: []
      },

      bulk: {
        kind: 'cod_pdf',
        // COD PDF flow: leaving Payment Date blank uses each row's delivered
        // date as the payment date. Setting it overrides that per row.
        paymentDate: '',
        file: null,
        canParse: false,
        parsing: false,
        submitting: false,
        matched: [],
        unmatched: [],
        selected: {},
        rowSubmitting: {},
        report: {
          loaded: false,
          invoiceNumber: '',
          reportDate: '',
          totalAmount: null
        }
      },

      pending: {
        fromDate: daysAgo(365),
        toDate: today,
        bucket: 'all',
        complaintFilter: 'all', // all | filed | awaiting
        loading: false,
        downloading: false,
        results: [],
        sum: 0,
        count: 0
      },

      paymentReport: {
        date: today,
        query: '',
        loading: false,
        loaded: false,
        mapOrders: [],
        mrOrders: [],
        mapSum: 0,
        mrSum: 0,
        mapCount: 0,
        mrCount: 0
      },

      salesReport: {
        fromDate: today,
        toDate: today,
        loading: false,
        loaded: false,
        amount: 0,
        count: 0
      },

      detailsModal: {
        show: false,
        loading: false,
        order: null,
        orderNumber: '',
        updatePaymentDate: today,
        updating: false,
        removing: false,
        error: ''
      },

      // Carries the last payment date the user committed forward to the next
      // order's modal. Finance approves many orders in a row (search by
      // tracking id → set date → save), so defaulting each new order to the
      // previously-used date saves re-picking it every time.
      lastPaymentDate: today,

      // Dedicated, focused Post Office Complaint modal — keeps the filing
      // flow (draft → copy → record id) out of the busy order-details view.
      complaintModal: {
        show: false,
        orderNumber: '',
        header: { orderNumber: '', customer: '—', postOffice: '—', amount: null, age: null },
        id: '',
        date: today,
        notes: '',
        by: '',
        filed: false,
        draft: '',
        saving: false,
        clearing: false,
        copied: false,
        error: ''
      },

      searchMethodOptions: [
        { value: 'order_number', label: 'Order Number' },
        { value: 'order_id', label: 'Order ID' },
        { value: 'tracking_id', label: 'Tracking ID' },
        { value: 'phone', label: 'Customer Phone' },
        { value: 'name', label: 'Customer Name' }
      ],
      bulkKindOptions: [
        { value: 'cod_pdf', label: 'COD PDF (Post Office)' },
        { value: 'csv', label: 'COD CSV' }
      ],
      searchColumns: [
        { key: 'order_number', label: 'Order Number' },
        { key: 'contact_name', label: 'Customer' },
        { key: 'contact_phone', label: 'Phone' },
        { key: 'payment_method', label: 'Method', width: '100px' },
        { key: 'grand_total', label: 'Amount', align: 'right', width: '130px' },
        { key: 'payment_status', label: 'Status', width: '150px' },
        { key: 'action', label: '', align: 'right', width: '120px' }
      ],
      pendingColumns: [
        { key: 'age', label: 'Age', align: 'center', width: '90px' },
        { key: 'order_number', label: 'Order Number' },
        { key: 'order_date', label: 'Order Date', width: '120px' },
        { key: 'customer', label: 'Customer' },
        { key: 'post_office', label: 'Post Office' },
        { key: 'contact_state', label: 'State', width: '130px' },
        { key: 'tracking_id', label: 'Tracking', width: '140px' },
        { key: 'grand_total', label: 'Amount', align: 'right', width: '120px' },
        { key: 'placed_by', label: 'Placed By', width: '150px' },
        { key: 'complaint', label: 'PO Complaint', width: '160px' },
        { key: 'action', label: '', align: 'right', width: '90px' }
      ],
      paymentReportColumns: [
        { key: 'order_number', label: 'Order Number' },
        { key: 'contact_name', label: 'Customer' },
        { key: 'contact_phone', label: 'Phone' },
        { key: 'tracking_id', label: 'Tracking', width: '140px' },
        { key: 'payment_method', label: 'Method', width: '90px' },
        { key: 'grand_total', label: 'Amount', align: 'right', width: '130px' }
      ],
      bulkColumns: [
        { key: 'order_id', label: 'Order ID', width: '110px' },
        { key: 'emo_id', label: 'EMO ID', width: '130px' },
        { key: 'name', label: 'Name' },
        { key: 'post_office', label: 'Post Office' },
        { key: 'amount', label: 'Amount', align: 'right', width: '120px' },
        { key: 'status', label: 'Status', width: '110px' }
      ],
      codBulkColumns: [
        { key: 'select', label: '', align: 'center', width: '44px' },
        { key: 'tracking_id', label: 'Article #', width: '160px' },
        { key: 'delivered_date_display', label: 'Delivered', width: '110px' },
        { key: 'cod_amount', label: 'COD Amount', align: 'right', width: '160px' },
        { key: 'order_number', label: 'Order #', width: '130px' },
        { key: 'contact_name', label: 'Customer' },
        { key: 'payment_status', label: 'Status', width: '150px' },
        { key: 'action', label: '', align: 'right', width: '150px' }
      ],
      codUnmatchedColumns: [
        { key: 'tracking_id', label: 'Article #', width: '160px' },
        { key: 'delivered_date_display', label: 'Delivered', width: '120px' },
        { key: 'cod_amount', label: 'COD Amount', align: 'right', width: '160px' },
        { key: 'reason', label: 'Reason' }
      ]
    }
  },
  computed: {
    searchInputLabel () {
      const opt = this.searchMethodOptions.find((o) => o.value === this.search.method)
      return opt ? `Enter ${opt.label}` : 'Enter Value'
    },
    // Decorate each pending order with its age (days since order date) and
    // aging bucket, sorted oldest-first so the most urgent surface on top.
    pendingRows () {
      const rows = (this.pending.results || []).map((r) => {
        const age = this.pendingAge(r.order_date)
        return { ...r, age, bucket: pendingBucketFor(age) }
      })
      rows.sort((a, b) => (b.age == null ? -1 : b.age) - (a.age == null ? -1 : a.age))
      return rows
    },
    // Count + amount totals per bucket (plus an "all" rollup) for the cards.
    pendingSummary () {
      const blank = () => ({ count: 0, sum: 0 })
      const summary = { all: blank(), fresh: blank(), aging: blank(), overdue: blank() }
      this.pendingRows.forEach((r) => {
        const amount = Number(r.grand_total || 0)
        summary.all.count += 1
        summary.all.sum += amount
        summary[r.bucket].count += 1
        summary[r.bucket].sum += amount
      })
      return summary
    },
    // Complaint coverage across the loaded pending set. "Awaiting" only
    // counts Overdue (30+) orders, since those are the ones that should
    // have a post-office complaint filed.
    complaintSummary () {
      let filedCount = 0; let filedSum = 0
      let awaitingCount = 0; let awaitingSum = 0
      this.pendingRows.forEach((r) => {
        const amount = Number(r.grand_total || 0)
        if (r.cod_complaint_id) {
          filedCount += 1; filedSum += amount
        } else if (r.bucket === 'overdue') {
          awaitingCount += 1; awaitingSum += amount
        }
      })
      return {
        filed: { count: filedCount, sum: filedSum },
        awaiting: { count: awaitingCount, sum: awaitingSum }
      }
    },
    visiblePendingRows () {
      let rows = this.pendingRows
      if (this.pending.bucket !== 'all') {
        rows = rows.filter((r) => r.bucket === this.pending.bucket)
      }
      if (this.pending.complaintFilter === 'filed') {
        rows = rows.filter((r) => !!r.cod_complaint_id)
      } else if (this.pending.complaintFilter === 'awaiting') {
        rows = rows.filter((r) => !r.cod_complaint_id && r.bucket === 'overdue')
      }
      return rows
    },
    pendingCards () {
      return [
        { key: 'all', label: 'Total Pending', range: 'All ages' },
        PENDING_BUCKETS.fresh,
        PENDING_BUCKETS.aging,
        PENDING_BUCKETS.overdue
      ]
    },
    // The Download button exports whichever aging category is currently
    // selected (the cards double as the export scope). "Total Pending"
    // downloads the full set, matching the original behaviour.
    activeBucketLabel () {
      const card = this.pendingCards.find((c) => c.key === this.pending.bucket)
      return card ? card.label : 'Total Pending'
    },
    downloadCsvLabel () {
      return this.pending.bucket === 'all'
        ? 'Download CSV'
        : `Download ${this.activeBucketLabel} CSV`
    },
    // Free-text filter for the COD Payment Report tables — matches the query
    // against tracking ID, order number, customer, phone, method and amount.
    filteredMapOrders () {
      return this.paymentReport.mapOrders.filter((o) => this.paymentReportMatch(o))
    },
    filteredMrOrders () {
      return this.paymentReport.mrOrders.filter((o) => this.paymentReportMatch(o))
    },
    bulkFileLabel () {
      if (this.bulk.kind === 'cod_pdf') return 'COD Commission PDF'
      if (this.bulk.kind === 'pdf') return 'VPP PDF'
      return 'COD CSV'
    },
    bulkFileAccept () {
      if (this.bulk.kind === 'cod_pdf' || this.bulk.kind === 'pdf') return 'application/pdf'
      return '.csv,text/csv'
    },
    // COD PDF row-selection derivatives. Kept as top-level computeds so the
    // template can read `codSelectedCount` etc. directly — Vue 2 only exposes
    // computeds at the component root, not nested under `bulk.*`.
    codPendingRows () {
      return (this.bulk.matched || []).filter((r) => !r.already_paid)
    },
    codSelectedCount () {
      return this.codPendingRows.filter((r) => this.bulk.selected[r.tracking_id]).length
    },
    codPendingCount () {
      return this.codPendingRows.length
    },
    codAllPendingSelected () {
      return this.codPendingCount > 0 && this.codSelectedCount === this.codPendingCount
    },
    codSomeSelected () {
      return this.codSelectedCount > 0 && !this.codAllPendingSelected
    }
  },
  mounted () {
    this.loadPending('view')
  },
  watch: {
    'bulk.file' (newFile) { this.bulk.canParse = !!newFile }
  },
  methods: {
    token () { return localStorage.getItem('api_token') },
    setActiveTab (key) {
      this.activeTab = key
      this.errorMessage = ''
      this.successMessage = ''
    },
    formatINR (value) {
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n)
    },
    formatLabel (value) {
      if (value === null || value === undefined || value === '') return '—'
      return String(value).replace(/_/g, ' ')
    },
    paymentStatusClass (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'payment_received' || s === 'paid') return 'badge--green'
      if (s === 'payment_pending') return 'badge--yellow'
      if (s === 'refunded' || s === 'cancelled') return 'badge--red'
      return 'badge--gray'
    },
    paymentBadgeClass (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'payment_received' || s === 'paid') return 'vo-pill--green'
      if (s === 'payment_pending') return 'vo-pill--yellow'
      if (s === 'refunded' || s === 'cancelled') return 'vo-pill--red'
      return 'vo-pill--gray'
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },

    // ─────────── Search Payments ───────────
    isOrderCancelled (order) {
      if (!order) return false
      if (order.deleted_at) return true
      return String(order.order_status || '').toLowerCase() === 'cancelled'
    },
    runSearch () {
      this.errorMessage = ''
      this.successMessage = ''
      const v = this.search.value.trim()
      if (!v) {
        this.errorMessage = 'Enter a search value.'
        return
      }

      const methodToColumn = {
        order_number: 'order_number',
        order_id: 'id',
        tracking_id: 'tracking_id',
        phone: 'contact_phone',
        name: 'contact_name'
      }
      const column = methodToColumn[this.search.method] || 'order_number'

      const payload = {
        search_parameter: column,
        search_value: v,
        start_date: '2020-01-01',
        end_date: isoToday(),
        minBottles: 1,
        maxBottles: 10000,
        user: 'any',
        order_type: 'any',
        state_name: 'all',
        district_name: 'all',
        local_body_name: 'all'
      }

      this.search.loading = true
      axios.post(`${this.$apiAdress}/api/orders/search_orders?token=${this.token()}`, payload)
        .then((response) => {
          const data = response.data
          if (typeof data === 'string') {
            this.search.results = []
            this.errorMessage = data
            return
          }
          const raw = Array.isArray(data) ? data : []
          const list = raw.filter((order) => !this.isOrderCancelled(order))
          this.search.results = list
          if (!list.length) {
            this.errorMessage = raw.length
              ? 'Matching orders found, but all are cancelled — not shown.'
              : 'No matching orders found.'
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to search orders.')
          this.search.results = []
        })
        .finally(() => { this.search.loading = false })
    },
    // ─────────── Order Details Modal ───────────
    orderItemTotal (item) {
      const price = Number(item?.price || 0)
      const qty = Number(item?.quantity || 0)
      const total = price * qty
      return Number.isFinite(total) ? total : '—'
    },
    totalBottles (order) {
      if (!order || !Array.isArray(order.order_items)) return '—'
      return order.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    },
    formatModalDate (value) {
      if (!value) return 'N/A'
      const ymd = this.toLocalDateInput(value)
      const parts = ymd.split('-')
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`
      return ymd
    },
    // The Order model casts payment_date / order_date / shipping_date as
    // `datetime`, which Laravel serialises to a UTC ISO instant. Because the
    // values are stored at Asia/Kolkata midnight, that UTC string lands on the
    // PREVIOUS calendar day (…T18:30:00Z), so naively slicing its date portion
    // showed the day before (saving 10-Jun displayed 09-Jun). A plain
    // "YYYY-MM-DD" (picker value / optimistic patch) is already a calendar date
    // and returned as-is; a full instant is converted to the local calendar
    // date so it matches what the reports (whereDate on the naive DB value) show.
    toLocalDateInput (value) {
      if (!value) return ''
      const str = String(value)
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
      const d = new Date(str.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return str.slice(0, 10)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    openOrderDetails (row) {
      this.detailsModal.show = true
      this.detailsModal.loading = true
      this.detailsModal.error = ''
      this.detailsModal.order = null
      this.detailsModal.orderNumber = row.order_number || ''
      // Default to the last date the user set, not today, so consecutive
      // approvals don't need the date re-picked. An already-paid order still
      // overrides this with its own payment_date below.
      this.detailsModal.updatePaymentDate = this.lastPaymentDate || isoToday()

      const orderNumber = row.order_number
      const orderId = row.id

      const fetchById = () => axios.get(`${this.$apiAdress}/api/orders/${orderId}?token=${this.token()}`)
      const fetchByNumber = () => axios.get(`${this.$apiAdress}/api/orders`, {
        params: { token: this.token(), order_number: orderNumber, per_page: 1 }
      })

      const request = orderId ? fetchById() : fetchByNumber()

      request
        .then((response) => {
          const data = response.data
          let order = null
          if (Array.isArray(data)) order = data[0] || null
          else if (data && Array.isArray(data.data)) order = data.data[0] || null
          else if (data && Array.isArray(data.orders)) order = data.orders[0] || null
          else if (data && typeof data === 'object' && data.id) order = data
          if (!order) {
            this.detailsModal.error = 'Order not found.'
            return
          }
          this.detailsModal.order = order
          if (order.payment_date) {
            this.detailsModal.updatePaymentDate = this.toLocalDateInput(order.payment_date)
          }
        })
        .catch((error) => {
          console.log(error)
          this.detailsModal.error = this.parseError(error, 'Unable to load order details.')
        })
        .finally(() => { this.detailsModal.loading = false })
    },
    closeOrderDetails () {
      this.detailsModal.show = false
      this.detailsModal.order = null
      this.detailsModal.error = ''
    },
    syncOrderInTables (orderNumber, patch) {
      const apply = (row) => {
        if (row && row.order_number === orderNumber) {
          Object.assign(row, patch)
        }
      }
      this.search.results.forEach(apply)
      this.pending.results.forEach(apply)
    },
    removePaymentFromModal () {
      const order = this.detailsModal.order
      if (!order || !order.order_number) return
      this.detailsModal.removing = true
      axios.post(`${this.$apiAdress}/api/clear_vpp_payment?token=${this.token()}`, {
        order_number: order.order_number
      })
        .then(() => {
          this.successMessage = `Payment removed for ${order.order_number}.`
          this.detailsModal.order = { ...order, payment_status: 'payment_pending', payment_date: null }
          this.syncOrderInTables(order.order_number, { payment_status: 'payment_pending', payment_date: null })
        })
        .catch((error) => {
          console.log(error)
          this.detailsModal.error = this.parseError(error, 'Unable to remove payment.')
        })
        .finally(() => { this.detailsModal.removing = false })
    },
    updatePaymentDateFromModal () {
      const order = this.detailsModal.order
      if (!order || !order.order_number || !this.detailsModal.updatePaymentDate) return
      this.detailsModal.updating = true
      const payload = {
        order_id: order.order_number,
        field: 'order_number',
        amount: Number(order.grand_total || 0),
        comments: 'Payment date updated from COD Payments',
        payment_date: this.detailsModal.updatePaymentDate,
        type: 'update'
      }
      axios.post(`${this.$apiAdress}/api/orders/mark_paid?token=${this.token()}`, payload)
        .then(() => {
          this.successMessage = `Payment date updated for ${order.order_number}.`
          // Remember this date so the next order's modal pre-fills with it.
          this.lastPaymentDate = this.detailsModal.updatePaymentDate
          const patch = { payment_status: 'payment_received', payment_date: this.detailsModal.updatePaymentDate }
          this.detailsModal.order = { ...order, ...patch }
          this.syncOrderInTables(order.order_number, patch)
        })
        .catch((error) => {
          console.log(error)
          this.detailsModal.error = this.parseError(error, 'Unable to update payment date.')
        })
        .finally(() => { this.detailsModal.updating = false })
    },

    // ─────────── Post Office Complaint ───────────
    isCodComplaintRelevant (order) {
      if (!order) return false
      const method = String(order.payment_method || '').toLowerCase()
      const status = String(order.payment_status || '').toLowerCase()
      return (method === 'vpp' || method === 'cod') && status === 'payment_pending'
    },
    setComplaintFilter (key) {
      this.pending.complaintFilter = this.pending.complaintFilter === key ? 'all' : key
    },
    // Opens the focused complaint modal. `row` may be a pending-table row
    // or a full order record (from the order-details modal) — both carry
    // the fields buildComplaintDraft and the header need.
    openComplaintModal (row) {
      const cm = this.complaintModal
      cm.error = ''
      cm.copied = false
      cm.saving = false
      cm.clearing = false
      cm.orderNumber = row.order_number || ''
      cm.id = row.cod_complaint_id || ''
      cm.date = row.cod_complaint_date || isoToday()
      cm.notes = row.cod_complaint_notes || ''
      // pendingPayments returns the filer's name; a raw Order record returns
      // the numeric user id — don't surface a bare id as the "filed by" name.
      const by = row.cod_complaint_by
      cm.by = (by && Number.isNaN(Number(by))) ? by : ''
      cm.filed = !!row.cod_complaint_id
      cm.draft = buildComplaintDraft(row)
      cm.header = {
        orderNumber: row.order_number || '—',
        customer: row.customer || row.contact_name || '—',
        postOffice: row.post_office || row.post_office_name || row.contact_postofficename || '—',
        amount: row.grand_total != null ? row.grand_total : null,
        age: typeof row.age === 'number' ? row.age : this.pendingAge(row.order_date)
      }
      cm.show = true
    },
    closeComplaintModal () {
      this.complaintModal.show = false
    },
    async copyComplaintDraft () {
      const ok = await copyToClipboard(this.complaintModal.draft || '')
      if (ok) {
        this.complaintModal.copied = true
        setTimeout(() => { this.complaintModal.copied = false }, 2000)
      } else {
        this.complaintModal.error = 'Could not copy — select the text and copy manually.'
      }
    },
    saveComplaint () {
      const cm = this.complaintModal
      const orderNumber = cm.orderNumber
      const complaintId = (cm.id || '').trim()
      if (!orderNumber) return
      if (!complaintId) {
        cm.error = 'Enter the complaint ID from the post office.'
        return
      }
      cm.error = ''
      cm.saving = true
      const payload = {
        order_number: orderNumber,
        complaint_id: complaintId,
        complaint_date: cm.date || isoToday(),
        complaint_notes: cm.notes || null
      }
      axios.post(`${this.$apiAdress}/api/cod_complaint?token=${this.token()}`, payload)
        .then((response) => {
          const d = response.data || {}
          this.successMessage = `Complaint ${d.cod_complaint_id || complaintId} recorded for ${orderNumber}.`
          cm.id = d.cod_complaint_id || complaintId
          cm.date = d.cod_complaint_date || payload.complaint_date
          cm.by = d.cod_complaint_by || cm.by
          cm.notes = d.cod_complaint_notes || ''
          cm.filed = true
          this.syncOrderInTables(orderNumber, {
            cod_complaint_id: cm.id,
            cod_complaint_date: cm.date,
            cod_complaint_by: cm.by,
            cod_complaint_notes: cm.notes
          })
        })
        .catch((error) => {
          console.log(error)
          cm.error = this.parseError(error, 'Unable to record complaint.')
        })
        .finally(() => { cm.saving = false })
    },
    clearComplaint () {
      const cm = this.complaintModal
      const orderNumber = cm.orderNumber
      if (!orderNumber) return
      cm.error = ''
      cm.clearing = true
      axios.post(`${this.$apiAdress}/api/cod_complaint/clear?token=${this.token()}`, { order_number: orderNumber })
        .then(() => {
          this.successMessage = `Complaint cleared for ${orderNumber}.`
          cm.id = ''
          cm.by = ''
          cm.notes = ''
          cm.filed = false
          cm.date = isoToday()
          this.syncOrderInTables(orderNumber, {
            cod_complaint_id: null,
            cod_complaint_date: null,
            cod_complaint_by: null,
            cod_complaint_notes: null
          })
        })
        .catch((error) => {
          console.log(error)
          cm.error = this.parseError(error, 'Unable to clear complaint.')
        })
        .finally(() => { cm.clearing = false })
    },

    // ─────────── Bulk ───────────
    resetBulkState () {
      this.bulk.file = null
      this.bulk.matched = []
      this.bulk.unmatched = []
      this.bulk.selected = {}
      this.bulk.rowSubmitting = {}
      this.bulk.report = { loaded: false, invoiceNumber: '', reportDate: '', totalAmount: null }
      if (this.$refs.bulkFileInput) this.$refs.bulkFileInput.value = ''
    },
    onBulkKindChange (kind) {
      this.bulk.kind = kind
      this.resetBulkState()
    },
    onBulkFileSelect (event) {
      const file = event.target.files && event.target.files[0]
      this.bulk.file = file || null
      // Preserve payment date but drop any previously parsed rows — a new file
      // means the current selection state no longer maps to anything real.
      this.bulk.matched = []
      this.bulk.unmatched = []
      this.bulk.selected = {}
      this.bulk.report.loaded = false
    },
    parseBulkFile () {
      if (!this.bulk.file) {
        this.errorMessage = 'Choose a file first.'
        return
      }
      if (this.bulk.kind === 'cod_pdf') { this.parseCodPdf(); return }
      if (this.bulk.kind !== 'pdf') {
        this.errorMessage = 'CSV parsing is not exposed by the backend — use Mark All As Paid directly once matched orders are loaded.'
        return
      }
      this.errorMessage = ''
      this.successMessage = ''
      this.bulk.parsing = true

      const form = new FormData()
      form.append('pdf', this.bulk.file)

      axios.post(`${this.$apiAdress}/api/uploadvpp?token=${this.token()}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then((response) => {
          const data = response.data || {}
          this.bulk.matched = Array.isArray(data.lines) ? data.lines : (Array.isArray(data) ? data : [])
          if (data.date && !this.bulk.paymentDate) this.bulk.paymentDate = data.date
          if (!this.bulk.matched.length) this.errorMessage = 'No orders matched in the uploaded file.'
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to parse the uploaded file.')
        })
        .finally(() => { this.bulk.parsing = false })
    },
    submitBulk () {
      if (!this.bulk.matched.length || !this.bulk.paymentDate) return
      this.bulk.submitting = true
      this.errorMessage = ''
      this.successMessage = ''

      const endpoint = this.bulk.kind === 'pdf' ? 'markvppaspaid' : 'markcsvaspaid'
      const payload = {
        matchedOrders: this.bulk.matched.map((m) => ({
          order_id: m.order_id,
          emo_id: m.emo_id || null
        })),
        payment_date: this.bulk.paymentDate
      }

      axios.post(`${this.$apiAdress}/api/${endpoint}?token=${this.token()}`, payload)
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : []
          const ok = list.filter((r) => r && r.save).length
          this.successMessage = `Marked ${ok}/${list.length || this.bulk.matched.length} orders as paid.`
          this.bulk.matched = []
          this.bulk.file = null
          if (this.$refs.bulkFileInput) this.$refs.bulkFileInput.value = ''
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk mark-as-paid failed.')
        })
        .finally(() => { this.bulk.submitting = false })
    },

    // ─────────── COD PDF (Post Office Commission Report) ───────────
    parseCodPdf () {
      this.errorMessage = ''
      this.successMessage = ''
      this.bulk.parsing = true
      const form = new FormData()
      form.append('pdf', this.bulk.file)

      axios.post(`${this.$apiAdress}/api/upload_cod_pdf?token=${this.token()}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then((response) => {
          const data = response.data || {}
          const matched = Array.isArray(data.matched) ? data.matched : []
          const unmatched = Array.isArray(data.unmatched) ? data.unmatched : []
          this.bulk.matched = matched
          this.bulk.unmatched = unmatched
          this.bulk.selected = {}
          this.bulk.rowSubmitting = {}
          // Pre-select every pending row so "Approve Selected" is one click
          // away for the common case (staff wants to record everything).
          matched.forEach((row) => {
            if (!row.already_paid) this.$set(this.bulk.selected, row.tracking_id, true)
          })
          this.bulk.report = {
            loaded: true,
            invoiceNumber: data.invoice_number || '',
            reportDate: data.report_date || '',
            totalAmount: data.total_amount != null ? Number(data.total_amount) : null
          }
          if (!matched.length && !unmatched.length) {
            this.errorMessage = 'No article rows extracted from the PDF.'
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to parse the uploaded COD PDF.')
        })
        .finally(() => { this.bulk.parsing = false })
    },
    codRowClass (row) {
      return row.already_paid ? 'cod-row cod-row--paid' : 'cod-row'
    },
    toggleCodRow (row, checked) {
      if (row.already_paid) return
      this.$set(this.bulk.selected, row.tracking_id, !!checked)
    },
    toggleSelectAllCod (checked) {
      const next = {}
      if (checked) {
        this.codPendingRows.forEach((r) => { next[r.tracking_id] = true })
      }
      this.bulk.selected = next
    },
    // A row's payment date defaults to its delivered date; the global Payment
    // Date input in the header overrides that for every row when set.
    codPaymentDateFor (row) {
      return (this.bulk.paymentDate && String(this.bulk.paymentDate).trim())
        || row.delivered_date
        || isoToday()
    },
    applyRowPatch (trackingId, patch) {
      const idx = this.bulk.matched.findIndex((r) => r.tracking_id === trackingId)
      if (idx === -1) return
      this.bulk.matched.splice(idx, 1, { ...this.bulk.matched[idx], ...patch })
    },
    recordCodPayment (row) {
      if (!row || row.already_paid) return
      this.$set(this.bulk.rowSubmitting, row.tracking_id, true)
      this.errorMessage = ''
      const paymentDate = this.codPaymentDateFor(row)
      const payload = {
        entries: [{
          order_number: row.order_number,
          tracking_id: row.tracking_id,
          payment_date: paymentDate
        }]
      }
      axios.post(`${this.$apiAdress}/api/markcodpdfaspaid?token=${this.token()}`, payload)
        .then((response) => {
          const results = (response.data && response.data.results) || []
          const r = results[0]
          if (r && r.status === 'success') {
            this.applyRowPatch(row.tracking_id, {
              payment_status: 'payment_received',
              payment_date: r.payment_date,
              already_paid: true
            })
            this.$set(this.bulk.selected, row.tracking_id, false)
            this.successMessage = `Payment recorded for ${row.order_number}.`
          } else if (r && r.status === 'skipped') {
            this.applyRowPatch(row.tracking_id, {
              payment_status: 'payment_received',
              payment_date: r.payment_date,
              already_paid: true
            })
            this.successMessage = `${row.order_number} was already paid.`
          } else {
            this.errorMessage = `Could not record payment for ${row.order_number}.`
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to record payment.')
        })
        .finally(() => { this.$set(this.bulk.rowSubmitting, row.tracking_id, false) })
    },
    submitCodBulk (scope) {
      const rows = scope === 'all_pending'
        ? this.codPendingRows
        : this.codPendingRows.filter((r) => this.bulk.selected[r.tracking_id])
      if (!rows.length) return

      this.bulk.submitting = true
      this.errorMessage = ''
      this.successMessage = ''

      const entries = rows.map((row) => ({
        order_number: row.order_number,
        tracking_id: row.tracking_id,
        payment_date: this.codPaymentDateFor(row)
      }))

      axios.post(`${this.$apiAdress}/api/markcodpdfaspaid?token=${this.token()}`, { entries })
        .then((response) => {
          const results = (response.data && response.data.results) || []
          let ok = 0
          let skipped = 0
          let failed = 0
          results.forEach((r) => {
            if (r.status === 'success') {
              ok++
              this.applyRowPatch(r.order_number ? this.trackingByOrder(r.order_number) : null, {
                payment_status: 'payment_received',
                payment_date: r.payment_date,
                already_paid: true
              })
            } else if (r.status === 'skipped') {
              skipped++
              this.applyRowPatch(this.trackingByOrder(r.order_number), {
                payment_status: 'payment_received',
                payment_date: r.payment_date,
                already_paid: true
              })
            } else {
              failed++
            }
          })
          this.bulk.selected = {}
          const bits = [`${ok} recorded`]
          if (skipped) bits.push(`${skipped} already paid`)
          if (failed) bits.push(`${failed} failed`)
          this.successMessage = bits.join(' · ')
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk record-payment failed.')
        })
        .finally(() => { this.bulk.submitting = false })
    },
    trackingByOrder (orderNumber) {
      if (!orderNumber) return null
      const hit = this.bulk.matched.find((r) => r.order_number === orderNumber)
      return hit ? hit.tracking_id : null
    },

    // ─────────── Pending Payments ───────────
    // order_date arrives from the backend as "dd-mm-yyyy".
    pendingAge (orderDate) {
      if (!orderDate) return null
      const parts = String(orderDate).split('-')
      if (parts.length !== 3) return null
      const [d, m, y] = parts.map(Number)
      if (!d || !m || !y) return null
      const ordered = new Date(y, m - 1, d)
      if (Number.isNaN(ordered.getTime())) return null
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      ordered.setHours(0, 0, 0, 0)
      return Math.max(0, Math.round((today - ordered) / 86400000))
    },
    pendingAgeLabel (age) {
      if (age == null) return '—'
      if (age === 0) return 'Today'
      return `${age}d`
    },
    pendingRowClass (row) {
      return `pending-row pending-row--${row.bucket}`
    },
    setPendingBucket (key) {
      this.pending.bucket = key
    },
    loadPending (type) {
      if (!this.pending.fromDate || !this.pending.toDate) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }
      this.errorMessage = ''
      this.successMessage = ''
      const payload = { fromDate: this.pending.fromDate, toDate: this.pending.toDate, type }
      // For downloads, scope the export to the selected aging category
      // (fresh / aging / overdue). 'all' streams every pending payment.
      const bucket = this.pending.bucket
      if (type === 'download') payload.bucket = bucket
      if (type === 'download') this.pending.downloading = true
      else this.pending.loading = true

      const config = { params: { token: this.token() } }
      if (type === 'download') config.responseType = 'blob'

      axios.post(`${this.$apiAdress}/api/pending_payments`, payload, config)
        .then((response) => {
          if (type === 'download') {
            const blob = new Blob([response.data], { type: 'text/csv' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = bucket && bucket !== 'all'
              ? `pending_payments_${bucket}.csv`
              : 'pending_payments.csv'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
            return
          }
          const data = response.data || {}
          this.pending.results = Array.isArray(data.orders) ? data.orders : []
          this.pending.sum = Number(data.sum || 0)
          this.pending.count = Number(data.count || this.pending.results.length)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load pending payments.')
          if (type !== 'download') {
            this.pending.results = []
            this.pending.sum = 0
            this.pending.count = 0
          }
        })
        .finally(() => {
          this.pending.loading = false
          this.pending.downloading = false
        })
    },

    // ─────────── VPP Payment Report ───────────
    paymentReportMatch (order) {
      const q = (this.paymentReport.query || '').trim().toLowerCase()
      if (!q) return true
      const fields = [
        order.tracking_id,
        order.order_number,
        order.contact_name,
        order.contact_phone,
        order.payment_method,
        order.grand_total
      ]
      return fields.some((f) => f != null && String(f).toLowerCase().includes(q))
    },
    loadPaymentReport () {
      if (!this.paymentReport.date) {
        this.errorMessage = 'Payment Date is required.'
        return
      }
      this.errorMessage = ''
      this.paymentReport.query = ''
      this.paymentReport.loading = true
      axios.post(`${this.$apiAdress}/api/payments_report?token=${this.token()}`, { date: this.paymentReport.date })
        .then((response) => {
          const data = response.data || {}
          this.paymentReport.mapOrders = Array.isArray(data.mapOrders) ? data.mapOrders : []
          this.paymentReport.mrOrders = Array.isArray(data.mrOrders) ? data.mrOrders : []
          this.paymentReport.mapSum = Number(data.mapSum || 0)
          this.paymentReport.mrSum = Number(data.mrSum || 0)
          this.paymentReport.mapCount = Number(data.mapCount || 0)
          this.paymentReport.mrCount = Number(data.mrCount || 0)
          this.paymentReport.loaded = true
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load payment report.')
          this.paymentReport.loaded = false
        })
        .finally(() => { this.paymentReport.loading = false })
    },

    // ─────────── VPP Sales Report ───────────
    loadSalesReport () {
      if (!this.salesReport.fromDate || !this.salesReport.toDate) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }
      this.errorMessage = ''
      this.salesReport.loading = true
      axios.post(`${this.$apiAdress}/api/vpp_sales_report?token=${this.token()}`, {
        fromDate: this.salesReport.fromDate,
        toDate: this.salesReport.toDate
      })
        .then((response) => {
          const data = response.data || {}
          this.salesReport.amount = Number(data.total_sales_amount || 0)
          this.salesReport.count = Number(data.total_sales_count || 0)
          this.salesReport.loaded = true
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load sales report.')
          this.salesReport.loaded = false
        })
        .finally(() => { this.salesReport.loading = false })
    }
  }
}
</script>

<style scoped>
.vpp-page { padding: 0.25rem 0; }

.vpp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.vpp-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }

.vpp-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.vpp-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.vpp-toolbar__title p { margin: 0; font-size: 0.78rem; color: #6b7280; }

.vpp-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
  cursor: pointer;
}

.vpp-tab-bar {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0.55rem 0.3rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
  max-width: 100%;
  margin: 0.5rem 0 1rem;
  -webkit-overflow-scrolling: touch;
}

.vpp-tab-bar::-webkit-scrollbar { display: none; }

.vpp-tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.vpp-tab:focus { outline: none; box-shadow: none; }

.vpp-tab:focus-visible { box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25); }

.vpp-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.vpp-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

.vpp-tab__label { line-height: 1; }

.vpp-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.vpp-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.vpp-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.vpp-section__actions { display: flex; align-items: center; gap: 0.6rem; }

.vpp-section__sub { font-size: 0.78rem; color: #6b7280; }

.vpp-section__body { padding: 1rem; }

.vpp-helper {
  margin: 0 0 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
  line-height: 1.5;
}

.vpp-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.vpp-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 220px;
  min-width: 0;
}

.vpp-filter--action {
  flex: 0 0 auto;
  min-width: 130px;
}

.vpp-filter--full { flex: 1 1 100%; }

.vpp-filter--file { flex: 1 1 260px; }

.vpp-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.vpp-file {
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.vpp-file:hover {
  border-color: #16a07a;
}

.vpp-file:focus-within {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.vpp-file__native {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.vpp-file__btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 0.95rem;
  background: #f1f5f9;
  color: #1f2a44;
  font-size: 0.78rem;
  font-weight: 600;
  border-right: 1px solid #d8dde6;
  transition: background 0.15s ease, color 0.15s ease;
}

.vpp-file:hover .vpp-file__btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  border-right-color: transparent;
}

.vpp-file__name {
  flex: 1 1 auto;
  padding: 0 0.75rem;
  font-size: 0.85rem;
  color: #2f3640;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vpp-file.is-empty .vpp-file__name {
  color: #9ca3af;
}

.vpp-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0 1rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.vpp-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.vpp-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.vpp-btn--ghost {
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #1f2a44;
}

.vpp-btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
}

.vpp-btn--danger {
  background: #dc3545;
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.18);
}

.vpp-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 6px 14px rgba(220, 53, 69, 0.28);
}

.vpp-btn--success {
  background: linear-gradient(135deg, #16a07a 0%, #1b8f5a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(27, 143, 90, 0.18);
}

.vpp-btn--success:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 6px 14px rgba(27, 143, 90, 0.28);
}

.vpp-btn:disabled { opacity: 0.7; cursor: progress; }

.vpp-row-btn {
  height: 28px;
  padding: 0 0.75rem;
  border-radius: 6px;
  font-size: 0.74rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #16a07a;
  color: #0e5b46;
}

.vpp-row-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  border-color: transparent;
}

.vpp-row-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.vpp-kpi-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.vpp-kpi-row--four { grid-template-columns: repeat(4, minmax(0, 1fr)); }

.vpp-kpi {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-left: 4px solid transparent;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.vpp-kpi__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b778d;
  margin-bottom: 0.25rem;
}

.vpp-kpi__value {
  font-size: 1.45rem;
  font-weight: 700;
  color: #18233a;
  line-height: 1.1;
}

.vpp-kpi--indigo  { border-left-color: #3f51b5; background: linear-gradient(140deg, #ffffff 60%, #eef1ff 100%); }
.vpp-kpi--cyan    { border-left-color: #0097a7; background: linear-gradient(140deg, #ffffff 60%, #e9fbff 100%); }
.vpp-kpi--violet  { border-left-color: #6a1b9a; background: linear-gradient(140deg, #ffffff 60%, #f5ecff 100%); }
.vpp-kpi--emerald { border-left-color: #1b8f5a; background: linear-gradient(140deg, #ffffff 60%, #eaf9f1 100%); }

@media (max-width: 992px) {
  .vpp-kpi-row--four { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 576px) {
  .vpp-kpi-row { grid-template-columns: minmax(0, 1fr); }
  .vpp-filter--action { flex: 1 1 100%; }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.67rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.badge--green  { background: #dff5ea; color: #0e5b46; }
.badge--yellow { background: #fef3c7; color: #92400e; }
.badge--red    { background: #fee2e2; color: #991b1b; }
.badge--gray   { background: #f1f5f9; color: #475569; }

/* ── Pending Payments aging dashboard ── */
.vpp-aging-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.vpp-aging-card {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-left: 4px solid #cbd5e1;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.vpp-aging-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
}

.vpp-aging-card:focus { outline: none; }

.vpp-aging-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vpp-aging-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.vpp-aging-card__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b778d;
}

.vpp-aging-card__range {
  font-size: 0.66rem;
  font-weight: 600;
  color: #9aa6b8;
  white-space: nowrap;
}

.vpp-aging-card__count {
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.05;
  color: #18233a;
}

.vpp-aging-card__amount {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
}

.vpp-aging-card--all     { border-left-color: #3f51b5; }
.vpp-aging-card--fresh   { border-left-color: #16a07a; }
.vpp-aging-card--aging   { border-left-color: #d97706; }
.vpp-aging-card--overdue { border-left-color: #dc2626; }

.vpp-aging-card--fresh .vpp-aging-card__count   { color: #0e5b46; }
.vpp-aging-card--aging .vpp-aging-card__count   { color: #b45309; }
.vpp-aging-card--overdue .vpp-aging-card__count { color: #b91c1c; }

.vpp-aging-card.is-active {
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12), inset 0 0 0 2px currentColor;
}

.vpp-aging-card--all.is-active     { color: #3f51b5; background: linear-gradient(140deg, #ffffff 55%, #eef1ff 100%); }
.vpp-aging-card--fresh.is-active   { color: #16a07a; background: linear-gradient(140deg, #ffffff 55%, #eafaf2 100%); }
.vpp-aging-card--aging.is-active   { color: #d97706; background: linear-gradient(140deg, #ffffff 55%, #fff5e6 100%); }
.vpp-aging-card--overdue.is-active { color: #dc2626; background: linear-gradient(140deg, #ffffff 55%, #fdeaea 100%); }

@media (max-width: 992px) {
  .vpp-aging-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 480px) {
  .vpp-aging-row { grid-template-columns: minmax(0, 1fr); }
}

/* Age pill in the table */
.age-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0.16rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.age-pill--fresh   { background: #dff5ea; color: #0e5b46; }
.age-pill--aging   { background: #ffedd5; color: #b45309; }
.age-pill--overdue { background: #fee2e2; color: #b91c1c; }

/* Bucket-tinted rows — the deep selectors reach BaseTable's own <tr>/<td> */
.pending-table >>> .base-table tbody tr.pending-row--fresh td   { background: #f4fbf7; }
.pending-table >>> .base-table tbody tr.pending-row--aging td   { background: #fff8ee; }
.pending-table >>> .base-table tbody tr.pending-row--overdue td { background: #fef4f4; }

.pending-table >>> .base-table.is-hover tbody tr.pending-row--fresh:hover td   { background: #e8f7ef; }
.pending-table >>> .base-table.is-hover tbody tr.pending-row--aging:hover td   { background: #fdeed5; }
.pending-table >>> .base-table.is-hover tbody tr.pending-row--overdue:hover td { background: #fde4e4; }

.pending-table >>> .base-table tbody tr.pending-row--fresh td:first-child   { box-shadow: inset 4px 0 0 #16a07a; }
.pending-table >>> .base-table tbody tr.pending-row--aging td:first-child   { box-shadow: inset 4px 0 0 #d97706; }
.pending-table >>> .base-table tbody tr.pending-row--overdue td:first-child { box-shadow: inset 4px 0 0 #dc2626; }

/* ── View Order Modal (mirrors Orders.vue design) ── */
.view-order-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.view-order-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.view-order-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.view-order-modal >>> .modal-body {
  padding: 1.1rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.view-order-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
  justify-content: flex-end;
}

.view-order-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 1.1rem;
  margin: 0;
  min-width: 90px;
}

.vo-state {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

.vo-state--error { color: #991b1b; }

.vo-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.vo-section,
.vo-grid,
.vo-items-wrap {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.vo-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0.75rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: center;
}

.vo-row:last-child { border-bottom: 0; }

.vo-section .vo-row:nth-child(even) { background: #fafbfd; }

.vo-key {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vo-val {
  font-size: 0.82rem;
  color: #374151;
  word-break: break-word;
}

.vo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.vo-grid .vo-row {
  grid-template-columns: 140px 1fr;
  border-right: 1px solid #eef1f7;
}

.vo-grid .vo-row:nth-child(2n) { border-right: 0; }

.vo-grid .vo-row:nth-last-child(-n+2) { border-bottom: 0; }

.vo-grid .vo-row:nth-child(4n-1),
.vo-grid .vo-row:nth-child(4n) { background: #fafbfd; }

.vo-items {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.vo-items th {
  background: #ffffff;
  padding: 0.7rem 0.95rem;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  border-bottom: 1px solid #eef1f7;
  letter-spacing: -0.01em;
}

.vo-items td {
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  color: #374151;
}

.vo-items tr:last-child td { border-bottom: 0; }

.vo-items__empty {
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

.vo-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.vo-pill--shipping { background: #dbeafe; color: #1e40af; }
.vo-pill--payment  { background: #1f2a44; color: #ffffff; text-transform: uppercase; }
.vo-pill--green    { background: #dff5ea; color: #0e5b46; }
.vo-pill--yellow   { background: #fef3c7; color: #92400e; }
.vo-pill--red      { background: #fee2e2; color: #991b1b; }
.vo-pill--gray     { background: #f1f5f9; color: #475569; }

.vo-update { background: #ffffff; }

.vo-row--update { grid-template-columns: 200px 1fr; }

/* ── Placed-by + complaint pills in the pending table ── */
.placed-by { font-size: 0.84rem; color: #374151; }

.cmp-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: filter 0.15s ease, box-shadow 0.15s ease;
}

.cmp-pill--filed {
  background: #dff5ea;
  color: #0e5b46;
  border-color: #b6e6cf;
}

.cmp-pill--file {
  background: #fff1f1;
  color: #b91c1c;
  border-color: #f7c9c9;
}

.cmp-pill--filed:hover,
.cmp-pill--file:hover { filter: brightness(0.97); }

.cmp-pill--filed:focus-visible,
.cmp-pill--file:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.cmp-pill--none {
  color: #9aa6b8;
  font-weight: 600;
  cursor: default;
}

/* ── Complaint filter chips (BaseTable toolbar) ── */
.cmp-filter { display: inline-flex; gap: 0.4rem; }

.cmp-filter__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 30px;
  padding: 0 0.7rem;
  border-radius: 999px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #475569;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cmp-filter__chip:hover { border-color: #cbd5e1; background: #f8fafc; }

.cmp-filter__chip:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.cmp-filter__chip.is-active {
  border-color: transparent;
  color: #ffffff;
  background: #475569;
}

.cmp-filter__chip.is-active .cmp-filter__num { background: rgba(255, 255, 255, 0.25); color: #ffffff; }

.cmp-filter__chip.is-active .cmp-filter__dot { box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4); }

.cmp-filter__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cmp-filter__dot--filed { background: #16a07a; }
.cmp-filter__dot--awaiting { background: #dc2626; }

.cmp-filter__num {
  min-width: 18px;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 800;
  text-align: center;
}

/* ── Focused Post Office Complaint modal ── */
.cmp-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.cmp-modal >>> .modal-header { border-bottom: 1px solid #eef1f7; background: #ffffff; padding: 0.95rem 1.15rem; }
.cmp-modal >>> .modal-header .modal-title { font-size: 1.05rem; font-weight: 700; color: #1f2a44; margin: 0; }
.cmp-modal >>> .modal-body { padding: 1.1rem; background: #fbfcfe; max-height: calc(100vh - 220px); overflow-y: auto; }
.cmp-modal >>> .modal-footer { border-top: 1px solid #eef1f7; background: #ffffff; padding: 0.7rem 1.15rem; gap: 0.5rem; justify-content: flex-end; }
.cmp-modal >>> .modal-footer .btn { border-radius: 8px; font-weight: 600; font-size: 0.85rem; padding: 0.45rem 1.1rem; margin: 0; min-width: 90px; }

.cmp-body { display: flex; flex-direction: column; gap: 0.85rem; }

.cmp-summary {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.75rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cmp-summary__main { display: flex; align-items: center; gap: 0.55rem; flex-wrap: wrap; }
.cmp-summary__order { font-size: 0.95rem; font-weight: 800; color: #1f2a44; }
.cmp-summary__meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; font-size: 0.82rem; color: #4b5563; }
.cmp-summary__dot { color: #cbd5e1; }
.cmp-summary__amt { font-weight: 700; color: #0e5b46; }

.cmp-alert { margin: 0; border-radius: 10px; }

.cmp-field { display: flex; flex-direction: column; gap: 0.3rem; }

.cmp-field__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cmp-label { font-size: 0.78rem; font-weight: 600; color: #4f5560; }
.cmp-req { color: #dc2626; }

.cmp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }

.cmp-meta {
  font-size: 0.8rem;
  color: #4b5563;
  background: #f4fbf7;
  border: 1px solid #d6efe2;
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
}

.cmp-copy-btn {
  height: 26px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cmp-copy-btn:hover { border-color: #16a07a; background: #f5fbf8; }
.cmp-copy-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.cmp-copy-btn--lg { height: 30px; padding: 0 0.9rem; font-size: 0.78rem; }

@media (max-width: 576px) {
  .cmp-grid { grid-template-columns: 1fr; }
}

/* ─── COD PDF bulk section ─── */
.vpp-label__hint {
  margin-left: 0.35rem;
  font-weight: 500;
  color: #94a3b8;
  font-size: 0.72rem;
}

.vpp-kpi__value--sm { font-size: 1rem; letter-spacing: 0; }

.cod-selection {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  padding: 0.3rem 0.65rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
}

.cod-check {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #16a07a;
}

.cod-check:disabled { cursor: not-allowed; opacity: 0.4; }

.cod-tracking {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.82rem;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cod-amt { font-weight: 600; color: #1f2a44; }
.cod-amt--mismatch { color: #b45309; }

.cod-mismatch-note {
  display: block;
  font-size: 0.7rem;
  color: #b45309;
  margin-top: 2px;
}

.cod-paid-on {
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 2px;
}

.cod-row--paid { background: #f9fafb; }
.cod-row--paid td { color: #6b7280; }

.cod-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  min-width: 118px;
  padding: 0 0.85rem;
  border-radius: 6px;
  border: 1px solid #16a07a;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  outline: none;
}

.cod-action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.24);
}

.cod-action-btn:focus { outline: none; }
.cod-action-btn::-moz-focus-inner { border: 0; }
.cod-action-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.cod-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cod-action-btn--disabled {
  background: #f3f4f6;
  color: #6b7280;
  border-color: #e5e7eb;
  cursor: not-allowed;
}
</style>
